import { HiX } from "react-icons/hi";
import { Link, useLocation } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "components/Auth-context";
import { MdLogout } from "react-icons/md";
import DashIcon from "components/icons/DashIcon";

const avatarUrl = (name, bg = "0d9488") =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "User")}&background=${bg}&color=fff&size=80&bold=true`;

const BaseSidebar = ({ open, onClose, routes, fetchUrl, renderMeta }) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!auth.userId || !fetchUrl) return;
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}${fetchUrl}${auth.userId}`,
          { headers: { Authorization: "Bearer " + auth.token } }
        );
        if (!res.ok) return;
        const data = await res.json();
        setUser(data.admin || data.student || null);
      } catch (_) {}
    };
    load();
  }, [auth.userId, fetchUrl]);

  useEffect(() => {
    setImgError(false);
  }, [user?.image]);

  const isActive = (path) => location.pathname.includes(path.replace("/*", ""));

  const navRoutes = routes.filter((r) => r.icon && r.path !== "logout");

  const displayName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
  const initials = displayName
    ? displayName.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "U";
  const avatarSrc =
    user?.image && !imgError
      ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/${user.image.replace(/^\//, "")}`
      : avatarUrl(displayName || initials);

  return (
    <div
      className={`fixed left-0 top-0 z-50 flex h-screen w-[260px] flex-col border-r border-cyan-900 bg-cyan-900 transition-transform duration-200 xl:translate-x-0 xl:z-10 ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <button
        onClick={onClose}
        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-white hover:bg-white/10 hover:text-white xl:hidden"
      >
        <HiX className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-4">
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-teal-600">
          <img
            src={avatarSrc}
            alt={displayName || "User"}
            onError={() => setImgError(true)}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">
            {displayName || "Loading…"}
          </p>
          {renderMeta && renderMeta(user)}
        </div>
        <span className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
      </div>

      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <span className="text-[11px] font-bold uppercase tracking-widest text-teal-500">
          The Correct Steps
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3">
        <ul className="flex flex-col gap-0.5">
          {navRoutes.map((route, i) => {
            const active = isActive(route.path);
            return (
              <li key={i}>
                <Link
                  to={route.layout + "/" + route.path.replace("/*", "")}
                  onClick={() => { if (window.innerWidth < 1280) onClose(); }}
                  className={`group flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold transition-all duration-300 ${
                    active
                      ? "bg-teal-500 text-white shadow-xl shadow-teal-500/20"
                      : "text-white hover:bg-white/10 hover:pl-5 hover:text-white"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-300 [&>svg]:h-4 [&>svg]:w-4 ${
                      active
                        ? "bg-white/20 text-white ring-1 ring-white/30"
                        : "bg-white/5 text-white group-hover:bg-teal-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-teal-500/40"
                    }`}
                  >
                    {route.icon || <DashIcon />}
                  </span>
                  {route.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t border-white/10 px-3 py-3">
        <button
          onClick={auth.logout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-500/10 hover:text-red-400"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/5">
            <MdLogout className="h-4 w-4" />
          </span>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default BaseSidebar;